import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { Loader2, ArrowLeft, User, Shield, Palette, Monitor, Sun, Moon, Calendar } from 'lucide-react';

const THEME_KEY = 'sapling-theme';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Phoenix',
  'America/Indiana/Indianapolis',
  'America/Puerto_Rico',
  'America/Halifax',
  'America/St_Johns',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Perth',
  'Pacific/Auckland',
  'UTC',
] as const;

function formatTimezone(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const label = tz.replace(/_/g, ' ').replace(/\//g, ' / ');
    return `${label} (${offset})`;
  } catch {
    return tz;
  }
}

export function SettingsPage() {
  const { user, session, profile, updatePassword, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Profile form state
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [profileSaving, setProfileSaving] = useState(false);

  // Security form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Preferences state
  const [theme, setThemeState] = useState<'system' | 'light' | 'dark'>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return 'system';
  });
  const [defaultView, setDefaultView] = useState<'dashboard' | 'transactions'>('dashboard');
  const [weekStartDay, setWeekStartDay] = useState<0 | 1>(1); // 0=Sunday, 1=Monday (default)
  const [prefsSaving, setPrefsSaving] = useState(false);

  // Initialize form from profile
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setCompanyName(profile.company_name ?? '');
      setPhone(profile.phone ?? '');
      setTimezone(profile.timezone ?? 'America/New_York');

      const prefs = profile.user_preferences ?? {};
      if (prefs.default_view === 'transactions') {
        setDefaultView('transactions');
      }
      if (prefs.theme === 'light' || prefs.theme === 'dark') {
        applyTheme(prefs.theme as 'light' | 'dark');
      }
      if (prefs.week_start_day === 0 || prefs.week_start_day === 1) {
        setWeekStartDay(prefs.week_start_day);
      }
    }
  }, [profile]);

  // Check if user signed up via OAuth only (no password)
  const isOAuthOnly = (() => {
    const providers = user?.app_metadata?.providers as string[] | undefined;
    if (!providers) return false;
    return providers.length > 0 && !providers.includes('email');
  })();

  // ---- Profile handlers ----

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      const response = await api.patch(
        '/profile',
        { full_name: fullName, company_name: companyName, phone, timezone },
        session?.access_token
      );

      if (!response.success) {
        toast({ title: 'Error', description: response.error ?? 'Failed to save profile', variant: 'destructive' });
        return;
      }

      await refreshProfile();
      toast({ title: 'Profile updated', description: 'Your changes have been saved.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save profile', variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  // ---- Security handlers ----

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }

    setPasswordSaving(true);
    try {
      // Verify current password first
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email ?? '',
        password: currentPassword,
      });

      if (signInError) {
        toast({ title: 'Error', description: 'Current password is incorrect', variant: 'destructive' });
        return;
      }

      // Update to new password
      const result = await updatePassword(newPassword);
      if (!result.success) {
        toast({ title: 'Error', description: result.error ?? 'Failed to update password', variant: 'destructive' });
        return;
      }

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: 'Password updated', description: 'Your password has been changed successfully.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update password', variant: 'destructive' });
    } finally {
      setPasswordSaving(false);
    }
  };

  // ---- Preferences handlers ----

  const applyTheme = (value: 'system' | 'light' | 'dark') => {
    setThemeState(value);
    const root = document.documentElement;

    if (value === 'system') {
      localStorage.removeItem(THEME_KEY);
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    } else {
      localStorage.setItem(THEME_KEY, value);
      root.classList.toggle('dark', value === 'dark');
    }
  };

  const handlePrefsSave = async () => {
    setPrefsSaving(true);
    try {
      const prefs = {
        ...(profile?.user_preferences ?? {}),
        theme,
        default_view: defaultView,
        week_start_day: weekStartDay,
      };

      const response = await api.patch(
        '/profile',
        { user_preferences: prefs },
        session?.access_token
      );

      if (!response.success) {
        toast({ title: 'Error', description: response.error ?? 'Failed to save preferences', variant: 'destructive' });
        return;
      }

      await refreshProfile();
      toast({ title: 'Preferences saved', description: 'Your preferences have been updated.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save preferences', variant: 'destructive' });
    } finally {
      setPrefsSaving(false);
    }
  };

  // ---- Loading state ----

  if (!profile && user) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-sapling-primary-100" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 py-8">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            {/* Page header */}
            <h1 className="font-display text-2xl font-bold sm:text-3xl">Settings</h1>
            <p className="mt-1 text-muted-foreground">
              Manage your account, security, and preferences.
            </p>

            {/* Tabs */}
            <Tabs defaultValue="profile" className="mt-8">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="profile" className="gap-2">
                  <User className="h-4 w-4 hidden sm:inline" />
                  Profile
                </TabsTrigger>
                <TabsTrigger value="security" className="gap-2">
                  <Shield className="h-4 w-4 hidden sm:inline" />
                  Security
                </TabsTrigger>
                <TabsTrigger value="preferences" className="gap-2">
                  <Palette className="h-4 w-4 hidden sm:inline" />
                  Preferences
                </TabsTrigger>
              </TabsList>

              {/* ==================== PROFILE TAB ==================== */}
              <TabsContent value="profile">
                <div className="mt-6 space-y-6">
                  <div className="rounded-card border bg-card p-6">
                    <h2 className="font-display text-lg font-semibold">Profile Information</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Update your personal details.
                    </p>

                    <div className="mt-6 space-y-4">
                      {/* Email (read-only) */}
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={user?.email ?? ''}
                          disabled
                          className="bg-muted"
                        />
                        <p className="text-xs text-muted-foreground">
                          Email is linked to your authentication account and cannot be changed here.
                        </p>
                      </div>

                      {/* Full Name */}
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name</Label>
                        <Input
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="Your full name"
                        />
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input
                          id="companyName"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Your company or business name"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      {/* Timezone */}
                      <div className="space-y-2">
                        <Label htmlFor="timezone">Timezone</Label>
                        <select
                          id="timezone"
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                          className="flex h-10 w-full rounded-btn border border-input bg-background px-3 py-2 text-sm font-body ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                              {formatTimezone(tz)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Save button */}
                    <div className="mt-6 flex justify-end">
                      <Button onClick={handleProfileSave} disabled={profileSaving}>
                        {profileSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ==================== SECURITY TAB ==================== */}
              <TabsContent value="security">
                <div className="mt-6 space-y-6">
                  <div className="rounded-card border bg-card p-6">
                    <h2 className="font-display text-lg font-semibold">Change Password</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Update your account password.
                    </p>

                    {isOAuthOnly ? (
                      <div className="mt-6 rounded-btn border border-dashed bg-muted/50 p-6 text-center">
                        <Shield className="mx-auto h-8 w-8 text-muted-foreground" />
                        <p className="mt-3 text-sm text-muted-foreground">
                          Your account uses Google sign-in. Password management is not available for OAuth accounts.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-6 space-y-4">
                        {/* Current Password */}
                        <div className="space-y-2">
                          <Label htmlFor="currentPassword">Current Password</Label>
                          <Input
                            id="currentPassword"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                          />
                        </div>

                        {/* New Password */}
                        <div className="space-y-2">
                          <Label htmlFor="newPassword">New Password</Label>
                          <Input
                            id="newPassword"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min. 8 characters)"
                          />
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">Confirm New Password</Label>
                          <Input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                          />
                        </div>

                        {/* Save button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            onClick={handlePasswordChange}
                            disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
                          >
                            {passwordSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Update Password
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ==================== PREFERENCES TAB ==================== */}
              <TabsContent value="preferences">
                <div className="mt-6 space-y-6">
                  {/* Theme */}
                  <div className="rounded-card border bg-card p-6">
                    <h2 className="font-display text-lg font-semibold">Theme</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choose your preferred color scheme.
                    </p>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      <button
                        onClick={() => applyTheme('system')}
                        className={`flex flex-col items-center gap-2 rounded-btn border p-4 transition-colors ${
                          theme === 'system'
                            ? 'border-sapling-primary-100 bg-sapling-primary-100/5 text-sapling-primary-100'
                            : 'border-input hover:bg-muted'
                        }`}
                      >
                        <Monitor className="h-6 w-6" />
                        <span className="text-sm font-medium">System</span>
                      </button>
                      <button
                        onClick={() => applyTheme('light')}
                        className={`flex flex-col items-center gap-2 rounded-btn border p-4 transition-colors ${
                          theme === 'light'
                            ? 'border-sapling-primary-100 bg-sapling-primary-100/5 text-sapling-primary-100'
                            : 'border-input hover:bg-muted'
                        }`}
                      >
                        <Sun className="h-6 w-6" />
                        <span className="text-sm font-medium">Light</span>
                      </button>
                      <button
                        onClick={() => applyTheme('dark')}
                        className={`flex flex-col items-center gap-2 rounded-btn border p-4 transition-colors ${
                          theme === 'dark'
                            ? 'border-sapling-primary-100 bg-sapling-primary-100/5 text-sapling-primary-100'
                            : 'border-input hover:bg-muted'
                        }`}
                      >
                        <Moon className="h-6 w-6" />
                        <span className="text-sm font-medium">Dark</span>
                      </button>
                    </div>
                  </div>

                  {/* Default View */}
                  <div className="rounded-card border bg-card p-6">
                    <div className="flex items-center gap-2">
                      <h2 className="font-display text-lg font-semibold">Default View</h2>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        Coming Soon
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choose which view to show when you open the app.
                    </p>

                    <div className="mt-4 space-y-3">
                      <label className="flex cursor-pointer items-center gap-3 rounded-btn border p-3 transition-colors hover:bg-muted">
                        <input
                          type="radio"
                          name="defaultView"
                          value="dashboard"
                          checked={defaultView === 'dashboard'}
                          onChange={() => setDefaultView('dashboard')}
                          className="h-4 w-4 text-sapling-primary-100 accent-sapling-primary-100"
                        />
                        <div>
                          <p className="text-sm font-medium">Task Dashboard</p>
                          <p className="text-xs text-muted-foreground">Overview with quick actions and summaries</p>
                        </div>
                      </label>
                      <label className="flex cursor-pointer items-center gap-3 rounded-btn border p-3 transition-colors hover:bg-muted">
                        <input
                          type="radio"
                          name="defaultView"
                          value="transactions"
                          checked={defaultView === 'transactions'}
                          onChange={() => setDefaultView('transactions')}
                          className="h-4 w-4 text-sapling-primary-100 accent-sapling-primary-100"
                        />
                        <div>
                          <p className="text-sm font-medium">Transaction List</p>
                          <p className="text-xs text-muted-foreground">Jump straight to recent transactions</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Week Start Day */}
                  <div className="rounded-card border bg-card p-6">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <h2 className="font-display text-lg font-semibold">Week Start Day</h2>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Choose which day your week begins. This affects weekly and biweekly budget periods.
                    </p>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <label className="flex cursor-pointer items-center gap-3 rounded-btn border p-3 transition-colors hover:bg-muted">
                        <input
                          type="radio"
                          name="weekStartDay"
                          value="1"
                          checked={weekStartDay === 1}
                          onChange={() => setWeekStartDay(1)}
                          className="h-4 w-4 text-sapling-primary-100 accent-sapling-primary-100"
                        />
                        <div>
                          <p className="text-sm font-medium">Monday</p>
                          <p className="text-xs text-muted-foreground">Mon - Sun</p>
                        </div>
                      </label>
                      <label className="flex cursor-pointer items-center gap-3 rounded-btn border p-3 transition-colors hover:bg-muted">
                        <input
                          type="radio"
                          name="weekStartDay"
                          value="0"
                          checked={weekStartDay === 0}
                          onChange={() => setWeekStartDay(0)}
                          className="h-4 w-4 text-sapling-primary-100 accent-sapling-primary-100"
                        />
                        <div>
                          <p className="text-sm font-medium">Sunday</p>
                          <p className="text-xs text-muted-foreground">Sun - Sat</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Save preferences */}
                  <div className="flex justify-end">
                    <Button onClick={handlePrefsSave} disabled={prefsSaving}>
                      {prefsSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Preferences
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Back to Dashboard */}
            <div className="mt-8">
              <Button
                variant="ghost"
                onClick={() => navigate({ to: '/app/dashboard' })}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
